import Icon, { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { useMount } from 'ahooks';
import { message, Modal, ModalProps, Select, Tabs, Tooltip } from 'antd';
import Empty from 'components/Empty';
import StatusButton from 'components/StatusButton';
import { addFakeConversationOrderSn } from 'hooks/business/conversation';
import { useTransAppProductName } from 'hooks/useTrans';
import { invert, sortBy, uniq } from 'lodash-es';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { createConversation } from 'services/api/refund';
import { buyerNickMask } from 'services/channels/demo';
import { HeadComponent } from 'views/Conversation/Sider/Head';
import { PreviewModalProvider } from 'views/Conversation/Sider/PreviewModal';
import { SiderRefContext } from 'views/Conversation/Sider/RefContext';
import TradeComponent from 'views/Conversation/Sider/TradeComponent';
import { TradeListContext } from 'views/Conversation/Sider/TradeContext';
import useTrade from 'views/Conversation/Sider/useTrade';
import { createFakeConversation } from 'views/Review/Context';
import useBuyerDetail from 'views/Review/hooks/useBuyerDetail';
import { reviewChannelMap } from 'utils/constants';
import { ConversationModalContent } from 'views/Setting/Remind/components/ConversationModal';
import { ReactComponent as ReturnTradeSvg } from './assets/returnTrade.svg';
import { ReactComponent as BadCommentSvg } from './assets/badComment.svg';
import { ReactComponent as MaliciousSvg } from './assets/malicious.svg';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';

export interface HasConversationRecord {
  hasConversation: true;
  conversationId: string;
  buyerNick: string;
  platform: General.PlatformUpperCaseTunnel;
  region: General.UpperCaseRegion;
  storeName: string;
  storeAlias?: string;
  internalStoreId: string;
}

export interface NoConversationRecord {
  hasConversation: false;
  buyerNick: string;
  storeName: string;
  storeAlias: string;
  externalBuyerId: string;
  externalStoreId: string;
  internalStoreId: string;
  externalTradeIds: string[];
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
}

export type ConversationRecord = HasConversationRecord | NoConversationRecord;

export function ConversationsModal(
  props: ModalProps & {
    buyer: ReviewAnalysis.Buyer;
    records: ConversationRecord[];
    actions?: ReactNode[];
  },
) {
  const { buyer, records: recordsProp, actions, ...otherProps } = props;

  const { t } = useTranslate();

  const { config } = useStoreNameConfig();

  const [records, setRecords] = useState(recordsProp);

  const { returnTradeStoreIds, badCommentStoreIds, recordsMap } = useMemo(() => {
    const returnTradeStoreIds = uniq(
      buyer.returnTrades.map(({ internalStoreId }) => internalStoreId),
    );
    const badCommentStoreIds = uniq(
      buyer.badComments.map(({ internalStoreId }) => internalStoreId),
    );
    const badStoreIds = uniq([...returnTradeStoreIds, ...badCommentStoreIds]);
    const goodStoreIds = uniq(
      (buyer.completeTrades || []).map(({ internalStoreId }) => internalStoreId),
    ).filter((_) => !badStoreIds.includes(_));

    const recordsMap = records.reduce(
      (res, record) => {
        if (badStoreIds.includes(record.internalStoreId)) {
          res.badRecords.push(record);
        } else if (goodStoreIds.includes(record.internalStoreId)) {
          res.goodRecords.push(record);
        }
        return res;
      },
      {
        badRecords: [] as ConversationRecord[],
        goodRecords: [] as ConversationRecord[],
      },
    );

    return {
      returnTradeStoreIds,
      badCommentStoreIds,
      recordsMap,
    };
  }, [buyer, records]);

  const [filter, setFilter] = useState(
    Object.keys(recordsMap).find((key) => recordsMap[key].length > 0),
  );

  const filteredRecords = useMemo(() => (filter ? recordsMap[filter] : []), [recordsMap, filter]);

  const [activeRecord, setActiveRecord] = useState(filteredRecords[0]);
  const [active, setActive] = useState(filteredRecords[0]);

  useEffect(() => {
    setTimeout(() => {
      setActive(activeRecord);
    }, 400);
  }, [activeRecord]);

  useEffect(() => {
    setRecords(recordsProp);
  }, [recordsProp]);

  useEffect(() => {
    setActiveRecord(filteredRecords[0]);
  }, [filteredRecords]);

  if (records.length === 0) {
    return <></>;
  }

  const select = (
    <Select
      bordered={false}
      dropdownMatchSelectWidth={false}
      value={filter}
      onChange={setFilter}
      options={Object.keys(recordsMap)
        .filter((key) => recordsMap[key].length > 0)
        .map((key) => {
          return {
            value: key,
            label: {
              badRecords: t('有风险行为的店铺'),
              goodRecords: t('无风险行为的店铺'),
            }[key],
          };
        })}
    />
  );

  const tabs = (
    <Tabs
      activeKey={activeRecord.internalStoreId}
      onChange={(key) =>
        setActiveRecord(
          filteredRecords.find(({ internalStoreId }) => internalStoreId === key) ||
            filteredRecords[0],
        )
      }
    >
      {filteredRecords.map((record) => {
        const { internalStoreId, platform, region, storeName, storeAlias } = record;
        const returnTrade = returnTradeStoreIds.includes(internalStoreId);
        const badComment = badCommentStoreIds.includes(internalStoreId);
        const title = isStoreAliasFirst(config, platform, region)
          ? storeAlias || storeName
          : storeName;
        return (
          <Tabs.TabPane
            key={internalStoreId}
            tab={
              <div>
                {title}
                {returnTrade && badComment ? (
                  <Tooltip title={t('当前店铺有差评&退货退款')}>
                    <Icon component={MaliciousSvg} className="cursor-pointer" />
                  </Tooltip>
                ) : returnTrade ? (
                  <Tooltip title={t('当前店铺有退货退款')}>
                    <Icon component={ReturnTradeSvg} className="cursor-pointer" />
                  </Tooltip>
                ) : badComment ? (
                  <Tooltip title={t('当前店铺有差评')}>
                    <Icon component={BadCommentSvg} className="cursor-pointer" />
                  </Tooltip>
                ) : null}
              </div>
            }
          />
        );
      })}
    </Tabs>
  );

  return (
    <Modal
      width="90%"
      title={
        <Title>
          {select}
          {tabs}
        </Title>
      }
      footer={null}
      {...otherProps}
    >
      {active.hasConversation ? (
        <ConversationModalContent record={active} {...otherProps}>
          {(chat, sider) => {
            return (
              <Content>
                {chat}
                <section>
                  {sider}
                  {actions && <footer>{actions}</footer>}
                </section>
              </Content>
            );
          }}
        </ConversationModalContent>
      ) : (
        <NoConversationContent
          key={active.internalStoreId}
          record={active}
          actions={actions}
          onCreateConversation={(conversation) => {
            setRecords(
              records.map((record) => {
                return record.internalStoreId !== active.internalStoreId
                  ? record
                  : {
                      hasConversation: true,
                      conversationId: conversation.conversationId,
                      buyerNick: record.buyerNick,
                      platform: conversation.channel,
                      region: conversation.region,
                      storeName: record.storeName,
                      storeAlias: conversation.storeAlias,
                      internalStoreId: record.internalStoreId,
                    };
              }),
            );
          }}
        />
      )}
    </Modal>
  );
}

function NoConversationContent({
  record,
  actions,
  onCreateConversation,
}: {
  record: NoConversationRecord;
  actions?: ReactNode[];
  onCreateConversation: (conversation: Conversation.Conversation) => void;
}) {
  const {
    buyerNick,
    storeName,
    externalBuyerId,
    externalStoreId,
    internalStoreId,
    externalTradeIds,
    platform,
    region,
  } = record;

  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();

  const siderRef = useRef(null);

  const {
    loading: buyerDetailLoading,
    item: buyerDetail,
    fetch: fetchBuyerDetail,
  } = useBuyerDetail();

  const useTradeResult = useTrade(internalStoreId);

  const {
    list: tradeList,
    loading: tradeListLoading,
    fetchList: fetchTradeList,
    setList: setTradeList,
  } = useTradeResult;

  useMount(() => {
    fetchBuyerDetail({
      orderId: externalTradeIds[0],
      storeId: internalStoreId,
      channel: invert(reviewChannelMap)[platform] as any,
      region,
    }).catch(() => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    });

    fetchTradeList(
      {
        page_no: 1,
        page_size: externalTradeIds.length,
        external_buyer_id: externalBuyerId,
        internal_store_id: internalStoreId,
        external_trade_ids: externalTradeIds.join(','),
      },
      platform,
    ).catch(() => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    });
  });

  const conversation = useMemo(() => {
    return createFakeConversation({
      storeId: internalStoreId,
      buyerNick: buyerNick,
      buyerAvatar: buyerDetail?.buyerDetail.buyerPicture,
      region,
      channel: platform,
    });
  }, [internalStoreId, buyerNick, buyerDetail, region, platform]);

  return (
    <Container>
      <div>
        <Empty description={t('当前店铺无会话详情～')}>
          <StatusButton
            type="primary"
            onClick={() => {
              return createConversation({
                externalBuyerId,
                buyerNick: buyerNick,
                region,
                externalStoreId,
                channel: platform,
                orderId: externalTradeIds[0],
                sourceAction: 'RATING',
              })
                .then((res) => {
                  if (res.mockExternal && res.channel === 'SHOPEE') {
                    addFakeConversationOrderSn(res.conversationId, externalTradeIds[0]);
                  }
                  onCreateConversation(res);
                })
                .catch(() => {
                  message.error(t('review.获取会话信息失败，请稍后再试'));
                });
            }}
          >
            {appProductNameT('review.contactBuyer')}
          </StatusButton>
        </Empty>
      </div>
      <section ref={siderRef}>
        <header>
          {buyerDetailLoading && <LoadingOutlined />}
          {buyerDetail && (
            <HeadComponent
              storeId={internalStoreId}
              info={
                {
                  buyerAvatar: buyerDetail.buyerDetail.buyerPicture,
                  buyerNick: buyerNickMask(buyerNick),
                  channel: platform,
                  region,
                  storeName,
                  storeAlias: buyerDetail.buyerDetail.aliasName,
                } as any
              }
            />
          )}
        </header>
        <div>
          {tradeListLoading && <LoadingOutlined />}
          {tradeList?.length > 0 && (
            <SiderRefContext.Provider value={siderRef}>
              <PreviewModalProvider>
                <TradeListContext.Provider
                  value={{
                    ...useTradeResult,
                    conversation,
                  }}
                >
                  {sortBy(tradeList, ({ createdTimeInMs }) => -Number(createdTimeInMs)).map(
                    (trade) => (
                      <TradeComponent
                        trade={trade}
                        onUpdate={(trade) => {
                          setTradeList((curList) => {
                            return curList.map((curTrade) => {
                              if (curTrade.internalTradeId === trade.internalTradeId) {
                                return trade;
                              }

                              return curTrade;
                            });
                          });
                        }}
                        conversation={conversation}
                        sendable={false}
                      />
                    ),
                  )}
                </TradeListContext.Provider>
              </PreviewModalProvider>
            </SiderRefContext.Provider>
          )}
        </div>
        <footer>{actions}</footer>
      </section>
    </Container>
  );
}

const Title = styled.div`
  display: flex;
  align-items: center;
  margin: -17px 0;
  margin-left: -24px;
  margin-right: 48px;
  > .ant-tabs {
    flex: 1 0 0;
    .ant-tabs-nav {
      margin-bottom: 0;
      .ant-tabs-nav-wrap {
        height: 56px;
      }
      .ant-tabs-tab-btn {
        .anticon {
          margin-left: 4px;
          font-size: 20px;
          vertical-align: middle;
        }
      }
    }
  }
`;

const Container = styled.div`
  display: flex;
  height: calc(100vh - 180px);
  margin: -24px;
  background-color: #f0f2f5;
  > div {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    border-right: 1px solid #d3d7e0;
  }
  > section {
    flex: 0 0 420px;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    > header {
      flex: 0 0 auto;
      height: 78px;
      margin-bottom: 16px;
      background: #fff;
    }
    > div {
      flex: 1 0 0;
      overflow: auto;
      > * {
        margin-bottom: 12px;
      }
    }
    > footer {
      flex: 0 0 auto;
      padding: 12px;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      background: #fff;
      box-shadow: inset 0 1px 0 0 #f0f2f5, 0 -2px 8px 0 #a7b2cc1a;
      > button {
        max-width: 182px;
        height: auto;
        white-space: pre-wrap;
      }
    }
  }
`;

const Content = styled.div`
  display: flex;
  width: 100%;
  > section {
    flex: 0 0 420px;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    > div {
      flex: 1 0 0;
      overflow: auto;
    }
    > footer {
      flex: 0 0 auto;
      padding: 12px;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      background: #fff;
      box-shadow: inset 0 1px 0 0 #f0f2f5, 0 -2px 8px 0 #a7b2cc1a;
      > button {
        max-width: 182px;
        height: auto;
        white-space: pre-wrap;
      }
    }
  }
`;

export default ConversationsModal;
